import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Heart, Users } from 'lucide-react';

const Doctors = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const doctors = [
    {
      name: 'Dr Massinissa GHELBOUN',
      image: 'https://customer-assets.emergentagent.com/job_luxe-medispa/artifacts/cjwnp8cv_massi.png',
      rpps: '10100953586',
      credentials: [
        'Médecin urgentiste',
        'Diplôme Universitaire d\'Implantation Capillaire',
        'Diplôme Universitaire d\'Agent Physique en Médecine Esthétique',
        'Diplôme Universitaire en Technique d\'Injection et de Comblement',
      ],
    },
    {
      name: 'Dr Nacer GHELBOUN',
      image: 'https://customer-assets.emergentagent.com/job_luxe-medispa/artifacts/vaq8z028_nacer.png',
      rpps: '10100732824',
      credentials: [
        'Médecin Généraliste',
        'Diplôme Universitaire d\'Implantation Capillaire',
        'Diplôme Universitaire d\'Agent Physique en Médecine Esthétique',
        'Diplôme Universitaire en Technique d\'Injection et de Comblement',
      ],
    },
  ];

  const features = [
    {
      icon: Award,
      title: 'Expertise',
      description: 'Formation continue et diplômes spécialisés',
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Dévouement à la beauté naturelle',
    },
    {
      icon: Users,
      title: 'Approche familiale',
      description: 'Deux frères, une vision commune',
    },
  ];

  return (
    <section
      id="doctors"
      ref={ref}
      className="section-padding bg-gradient-to-b from-gray-50 to-white"
      data-testid="doctors-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl sm:text-6xl font-light text-gray-900 mb-6">
            Nos <span className="gradient-text font-normal">Médecins</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Deux frères, deux médecins unis par la passion de sublimer votre beauté naturelle
          </p>
        </motion.div>

        {/* Doctors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
          {doctors.map((doctor, index) => (
            <motion.div
              key={doctor.name}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-white rounded-3xl overflow-hidden shadow-lg elegant-hover"
              data-testid={`doctor-card-${index}`}
            >
              <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                <img
                  src={doctor.image}
                  alt={`${doctor.name} - Médecin médecine esthétique MN Rouvroy`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">{doctor.name}</h3>
                <p className="text-sm text-[#1e3a5f] mb-4">N° RPPS : {doctor.rpps}</p>
                <ul className="space-y-3">
                  {doctor.credentials.map((credential, i) => (
                    <li key={i} className="text-gray-600 flex items-start">
                      <span className="text-[#1e3a5f] mr-2">•</span>
                      <span className="text-sm leading-relaxed">{credential}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 + index * 0.1 }}
              className="text-center p-8 rounded-2xl bg-white shadow-md"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1e3a5f]/10 mb-6">
                <feature.icon className="w-8 h-8 text-[#1e3a5f]" />
              </div>
              <h4 className="text-xl font-medium text-gray-900 mb-2">{feature.title}</h4>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Doctors;